import React, { useState, useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import Navbar from "./Components/Navbar";
import Home from "./pages/scoreboard";
import Staff from "./pages/Staff";
import { SignIn } from "./Auth/SignIn";
import { Callback } from "./Auth/Callback";
import { checkToken } from "./support";
import axios from "axios";
import Ranking from "./pages/Ranking";

export interface BasicProps {
	mobile: boolean;
	vertical: boolean;
	setShowCover: (show: boolean) => void;
}

export interface MatchData {
	match_id: string;
	sport: string;
	category: string;
	group?: string;
	stage?: string;
	team1Name: string;
	team2Name: string;
	score1: number;
	score2: number;
	timer: string;
	quarterOrSet: number;
	fouls1: number;
	fouls2: number;
	location: string;
	status: string;
	startTime: string;
	endTime?: string;
}

const App: React.FC = () => {
	// MUI Configuration
	const theme = useTheme();
	const mobile = useMediaQuery(theme.breakpoints.down("md"));
	const vertical = useMediaQuery(theme.breakpoints.down("sm"));

	// Router Hooks
	const location = useLocation();
	const navigate = useNavigate();
	const [params] = useSearchParams();

	// State Management
	const [showCover, setShowCover] = useState(false);
	const [matches, setMatches] = useState<Array<MatchData>>([]);
	const [loadingMatches, setLoadingMatches] = useState<boolean>(true);
	const [elapsedTime, setElapsedTime] = useState(0);
	const [isRunning, setIsRunning] = useState(false);

	const [isStaff, setIsStaff] = useState(false);

	const sortMatches = (matches: MatchData[]): MatchData[] => {
		// Define status priority order
		const statusOrder: { [key: string]: number } = {
			'Playing': 1,
			'Soon': 2,
			'Ended': 3,
			'Not Started': 4
		};

		// Create a copy to avoid mutating original array
		return [...matches].sort((a, b) => {
			// First sort by status priority
			const statusComparison = statusOrder[a.status] - statusOrder[b.status];
			if (statusComparison !== 0) return statusComparison;

			// Then sort by start time (earlier first)
			return a.startTime.localeCompare(b.startTime);
		});
	};

	const API_URL = process.env.REACT_APP_API_URL;

	// 🟢 Function to Fetch Match Data from API
	const fetchMatches = async () => {
		try {
			//console.log("🔹 Fetching matches...");
			const response = await axios.post(`${API_URL}/get-data`, {
				token: localStorage.getItem("token"),
			});

			//console.log("🟢 Full API Response:", response);
			if (response.data && response.data.data && response.data.data.matches) {
				//console.log("✅ Match Data Found:", response.data.data.matches);
				const formattedMatches = response.data.data.matches.map((match: any) => ({
					match_id: match.match_id || "",
					sport: match.sport || "",
					category: match.category || "Mixed",
					group: match.group || "",
					stage: match.stage || "",
					team1Name: match.team1Name || "Unknown Team",
					team2Name: match.team2Name || "Unknown Team",
					score1: match.score1 ?? 0, // ✅ Use `??` to handle `null` values
					score2: match.score2 ?? 0,
					timer: match.timer || "10:00",
					quarterOrSet: match.quarterOrSet ?? 1,
					fouls1: match.foul1 ?? 0,
					fouls2: match.foul2 ?? 0,
					location: match.location || "Unknown Location",
					status: match.status || "Not Started",
					startTime: match.startTime || "00:00",
					endTime: match.endTime || "",
				}));

				console.log(isRunning);

				setMatches(sortMatches(formattedMatches));
			} else {
				//console.warn("⚠️ No match data found in API response:", response.data);
			}
		} catch (error) {
			//console.error("❌ Error fetching match data:", error);
		} finally {
			setLoadingMatches(false);
		}
	};

	useEffect(() => {
		doTokenCheck();
	}, [location]);

	// 🟢 Fetch Data Every Second
	useEffect(() => {
		fetchMatches().then(r => {}); // Initial Fetch

		window.addEventListener("storage", () => {
			const token = localStorage.getItem("token");
			console.log("UPDATED!", token, location.pathname)
			if (location.pathname !== "/callback" && token !== null) {
				setShowCover(true);
				doTokenCheck()
			} else if (location.pathname === "/sign-in" && token === null) {
				setShowCover(false);
			}
		}) // eslint-disable-next-line react-hooks/exhaustive-deps

		axios.post(`${API_URL}/is-staff`, {
			token: localStorage.getItem("token"),
		}).then((response) => {
			setIsStaff(response.data.data.staff);
		})

		const interval = setInterval(() => {
			fetchMatches().then(r => {}); // Fetch Data Every 1 Second
		}, 4000);

		return () => clearInterval(interval); // Cleanup on Unmount
	}, []);

	function doTokenCheck() {
		checkToken().then((valid) => {
			console.log("DO TOKEN CHECK", valid, location.search)
			// setShowCover(false);
			if (!valid && !["/sign-in", "/sign-in/", "/callback"].includes(location.pathname)) {
				const url = new URL(window.location.protocol + '//' + window.location.host + "/sign-in")

				if (location.pathname !== "/") {
					url.searchParams.set("nav", location.pathname);
				}

				params.forEach((value, key) => {
					if (key !== "nav" && key !== "req") {
						url.searchParams.set(key, value);
					}
				})

				window.location.href = url.href;
			} else if (valid && location.pathname === "/sign-in") {
				if (params.has("nav")) {
					const cleanedParams = params;
					const nav = (params.get("nav") as string).replace("$", "/");
					cleanedParams.delete("nav");

					console.log("NAV1")

					navigate({
						pathname: nav,
						search: cleanedParams.toString()
					});
				} else {
					console.log("NAV2")

					navigate({
						pathname: "/",
						search: params.toString()
					});
				}
			} else if (!valid && !["/sign-in", "/callback"].includes(location.pathname)) {
				console.log("NAV3", location.pathname)
				navigate("/sign-in")
			}
		})
	}

	// 🟢 Function to update match data (for Staff.tsx)
	const updateMatch = (updatedMatch: MatchData) => {
		setMatches(matches.map((match: MatchData) =>
			match.match_id === updatedMatch.match_id ? updatedMatch : match
		))
	};

	// 🟢 Token Check Logic
	useEffect(() => {
		const doTokenCheck = async () => {
			const valid = await checkToken();
			if (!valid && !["/sign-in", "/sign-in/", "/callback"].includes(location.pathname)) {
				navigate("/sign-in");
			}
		};

		doTokenCheck();
	}, [location]);

	return (
		<>
			<Navbar isStaff={isStaff} />
			<Routes>
				<Route path="/sign-in" element={<SignIn mobile={mobile} vertical={vertical} setShowCover={setShowCover} />} />
				<Route path="/callback" element={<Callback mobile={mobile} vertical={vertical} setShowCover={setShowCover} />} />
				<Route path="/" element={<Home matches={matches} elapsedTime={elapsedTime} />} />
				<Route path="/ranking" element={<Ranking />} />
				<Route
					path="/staff"
					element={
						<Staff
							matches={matches}
							updateMatch={updateMatch}
							elapsedTime={elapsedTime}
							setElapsedTime={setElapsedTime}
							isRunning={isRunning}
							setIsRunning={setIsRunning}
							refreshMatches={fetchMatches}
						/>
					}
				/>
			</Routes>
		</>
	);
};

const WrappedApp: React.FC = () => (
	<Router>
		<App />
	</Router>
);

export default WrappedApp;
